import React, { useEffect, useState } from 'react';
import { Link2, Plus, Search, Trash2 } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { NumericInput, ScanTextInput } from '../../../../common/inputs';
import { docInterpretationService } from '../../../../../services/docInterpretationService';
import type {
  DocInterpretationLine,
  DocInterpretationOrderLineHit,
  DocInterpretationReceiptLine,
  DocInterpretationResult,
} from '../../../../../types/docInterpretation';
import { effectiveQuantity, receiptLinesFor, statusClasses } from './docInterpretationHelpers';
import { NewOrderLinePanel } from './NewOrderLinePanel';

interface DocLineDetailProps {
  result: DocInterpretationResult;
  line: DocInterpretationLine;
  /** Dokumentnummeret skrives i InboundShipmentNo ved OK (kundens oppsett). */
  writesInboundShipmentNo: boolean;
  /** Kjører et kall som returnerer oppdatert tolking, og viser feil. */
  run: (action: () => Promise<DocInterpretationResult>) => Promise<void>;
  busy: boolean;
  /** Mottakslinjene har ulagrede endringer — OK sperres så de ikke går tapt (OK kan ikke angres). */
  onDirtyChange?: (dirty: boolean) => void;
}

const fmt = (value: number | null | undefined) =>
  value == null ? '–' : Number(value).toLocaleString('nb-NO', { maximumFractionDigits: 4 });

/**
 * Avgjørelser for én dokumentlinje (TW 36423840, punkt 16–19): antallsavvik, konflikt,
 * manuell kobling med søk, hold utenfor, og antall/batch per mottakslinje.
 * «Opprett ny bestillingslinje» venter på TW 36423842 og vises som ikke tilgjengelig.
 */
export const DocLineDetail: React.FC<DocLineDetailProps> = ({ result, line, writesInboundShipmentNo, run, busy, onDirtyChange }) => {
  const { t } = useTranslation();
  const p = 'manager.modules.workingOrders.docInterpretation.detail';
  const id = result.id!;
  const editable = result.status === 'Interpreted';

  const [customQuantity, setCustomQuantity] = useState('');
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<DocInterpretationOrderLineHit[] | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [receiptDraft, setReceiptDraft] = useState<{ quantity: string; batchNo: string; expiryDate: string }[]>([]);
  const [editNewLine, setEditNewLine] = useState(false);

  useEffect(() => {
    setCustomQuantity('');
    setEditNewLine(false);
    setHits(null);
    setSearchError(null);
    setQuery(line.docSupplierItemNo ?? line.docDescription ?? '');
    setReceiptDraft(receiptLinesFor(line).map(r => ({ quantity: String(r.quantity), batchNo: r.batchNo ?? '', expiryDate: r.expiryDate ?? '' })));
  }, [line]);

  const search = async () => {
    if (!query.trim()) return;
    setSearchError(null);
    try {
      setHits(await docInterpretationService.searchOrderLines(id, query.trim()));
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : String(err));
    }
  };

  // Koblet til en bestillingslinje, eller valgt «ny bestillingslinje» (opprettes ved OK)
  const newOrderLine = line.notFoundChoice === 'NewOrderLine' && line.orderLineNo == null;
  const linked = line.orderNo != null && line.orderLineNo != null;
  // Gjenstår: før OK det tolkingen regnet (fratrukket andre linjer i dokumentet), etter OK slik bestillingslinjen står nå
  const remainingShown = editable || line.orderedQuantity == null
    ? line.remainingQuantity
    : Math.max(0, line.orderedQuantity - (line.receivedQuantity ?? 0));
  const quantityDeviation = line.checks.includes('QuantityOverRest') || line.checks.includes('QuantityMissing');
  const notFound = line.status === 'NotFound';
  const quantity = effectiveQuantity(line);

  const receiptLines: DocInterpretationReceiptLine[] = receiptDraft
    .map(r => ({ quantity: Number(r.quantity), batchNo: r.batchNo.trim() || null, expiryDate: r.expiryDate || null }))
    .filter(r => r.quantity > 0);
  const receiptDirty = JSON.stringify(receiptLines) !== JSON.stringify(receiptLinesFor(line));

  useEffect(() => { onDirtyChange?.(receiptDirty); }, [receiptDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const conflictText = (type: string | null) => {
    switch (type) {
      case 'OtherDocumentNo': return t(`${p}.conflictOtherDocumentNo`, {}, 'Bestillingslinjen er merket med et annet dokumentnummer.');
      case 'ArrivalRegistered': return t(`${p}.conflictArrivalRegistered`, {}, 'Bestillingslinjen er allerede ankomstregistrert.');
      case 'CollectiveReceipt': return t(`${p}.conflictCollectiveReceipt`, {}, 'Bestillingslinjen ligger i et samlemottak.');
      default: return '';
    }
  };

  const button = (active: boolean) =>
    `px-3 py-2 text-sm rounded-lg border ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'} disabled:text-gray-400`;

  return (
    <div className="space-y-4 text-sm">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">
          {t(`${p}.lineTitle`, { '0': line.lineSeq }, `Linje ${line.lineSeq}`)}
        </h3>
        <span className={`px-2 py-0.5 text-xs font-semibold border rounded-full ${statusClasses(line.status)}`}>
          {t(`manager.modules.workingOrders.docInterpretation.status.${line.status}`, {}, line.status)}
        </span>
      </div>

      {/* I dokumentet */}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700">
        <dt className="text-gray-500">{t(`${p}.docItem`, {}, 'Varenr. i dokumentet')}</dt><dd>{line.docSupplierItemNo ?? '–'}</dd>
        <dt className="text-gray-500">{t(`${p}.docEan`, {}, 'EAN')}</dt><dd>{line.docEan ?? '–'}</dd>
        <dt className="text-gray-500">{t(`${p}.docDescription`, {}, 'Beskrivelse')}</dt><dd>{line.docDescription ?? '–'}</dd>
        <dt className="text-gray-500">{t(`${p}.docQuantity`, {}, 'Antall i dokumentet')}</dt><dd>{fmt(line.docQuantity)} {line.docUnit ?? ''}</dd>
        <dt className="text-gray-500">{t(`${p}.docBatch`, {}, 'Batch i dokumentet')}</dt><dd>
          {line.docBatchNo ?? '–'}
          {line.docBatchNo && line.itemNo && !line.batchItem && (
            <span className="text-gray-500"> · {t(`${p}.batchNotUsed`, {}, 'varen er ikke batchstyrt, batchen registreres ikke')}</span>
          )}
        </dd>
        <dt className="text-gray-500">{t(`${p}.docExpiry`, {}, 'Best før i dokumentet')}</dt><dd>{line.docExpiryDate ? new Date(line.docExpiryDate).toLocaleDateString('nb-NO') : '–'}</dd>
        {line.itemNo && (
          <>
            <dt className="text-gray-500">{t(`${p}.ourItem`, {}, 'Vår vare')}</dt>
            <dd className="font-medium">{line.itemNo}{line.itemDescription ? ` · ${line.itemDescription}` : ''}</dd>
            {line.matchMethod && (
              <>
                <dt className="text-gray-500">{t(`${p}.matchedBy`, {}, 'Gjenkjent via')}</dt>
                <dd>{t(`${p}.matchMethods.${line.matchMethod}`, {}, line.matchMethod)}</dd>
              </>
            )}
          </>
        )}
      </dl>

      {/* På bestillingen: bestilt, allerede mottatt og hva som gjenstår */}
      {linked && line.orderedQuantity != null && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700">
          <dt className="text-gray-500">{t(`${p}.ordered`, {}, 'Bestilt på bestillingen')}</dt><dd>{fmt(line.orderedQuantity)}</dd>
          <dt className="text-gray-500">{t(`${p}.received`, {}, 'Allerede mottatt')}</dt><dd>{fmt(line.receivedQuantity ?? 0)}</dd>
          {/* Andre linjer i samme dokument mot samme bestillingslinje tar av det som gjenstår */}
          {editable && remainingShown != null && line.orderedQuantity - (line.receivedQuantity ?? 0) - remainingShown > 0 && (
            <>
              <dt className="text-gray-500">{t(`${p}.takenByOtherLines`, {}, 'Tatt av andre linjer i dokumentet')}</dt>
              <dd>{fmt(line.orderedQuantity - (line.receivedQuantity ?? 0) - remainingShown)}</dd>
            </>
          )}
          <dt className="text-gray-500">{t(`${p}.remainingLabel`, {}, 'Gjenstår')}</dt><dd className="font-semibold">{fmt(remainingShown)}</dd>
        </dl>
      )}

      {/* Ny bestillingslinje: opprettes ved OK */}
      {newOrderLine && !line.excluded && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 space-y-1">
          <div className="font-semibold">{editable ? t(`${p}.onOk`, {}, 'Ved OK') : t(`${p}.onApproved`, {}, 'Ved godkjenning')}</div>
          <div>{t(`${p}.onOkNewOrderLine`, { '0': line.orderNoERP || line.orderNo!, '1': line.itemNo ?? '' },
            `Ny bestillingslinje på ${line.orderNoERP || line.orderNo} · ${line.itemNo}`)}</div>
          <div>{t(`${p}.newOrderLineQuantity`, { '0': fmt(quantity), '1': line.docUnit ?? '' }, `Antall: ${fmt(quantity)} ${line.docUnit ?? ''}`)}</div>
        </div>
      )}

      {/* Hva som skrives ved OK (punkt 19) */}
      {linked && !line.excluded && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 space-y-1">
          <div className="font-semibold">{editable ? t(`${p}.onOk`, {}, 'Ved OK') : t(`${p}.onApproved`, {}, 'Ved godkjenning')}</div>
          <div>{t(`${p}.onOkOrderLine`, { '0': line.orderNoERP || line.orderNo!, '1': line.orderLineNo!, '2': line.itemNo ?? '' },
            `Bestilling ${line.orderNoERP || line.orderNo} linje ${line.orderLineNo} · ${line.itemNo}`)}</div>
          <div>
            {editable
              ? t(`${p}.onOkQuantity`, { '0': fmt(quantity), '1': fmt(remainingShown) }, `Antall ${fmt(quantity)} (gjenstår ${fmt(remainingShown)})`)
              : t(`${p}.newOrderLineQuantity`, { '0': fmt(quantity), '1': line.docUnit ?? '' }, `Antall: ${fmt(quantity)} ${line.docUnit ?? ''}`)}
          </div>
          {writesInboundShipmentNo && result.documentNo && (
            <div>{t(`${p}.onOkInboundShipmentNo`, { '0': result.documentNo }, `Dokumentnummer ${result.documentNo} skrives på bestillingslinjen`)}</div>
          )}
          {line.serialItem && (
            <div>{t(`${p}.onOkSerialItem`, {}, 'Serienummervare: ankomstregistreres ikke. Serienumrene registreres i varemottaket.')}</div>
          )}
        </div>
      )}

      {/* Antallsavvik (punkt 16) */}
      {(linked || newOrderLine) && !line.excluded && editable && (
        <div className={`p-3 border rounded-lg space-y-2 ${quantityDeviation && !line.quantityChoice ? 'border-amber-200 bg-amber-50' : 'border-gray-200'}`}>
          <div className="font-semibold text-gray-800">
            {quantityDeviation
              ? t(`${p}.quantityDeviation`, {}, 'Antallet er høyere enn det som gjenstår på bestillingen — velg antall')
              : t(`${p}.quantity`, {}, 'Antall')}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy || line.docQuantity == null} className={button(line.quantityChoice === 'Document')}
              onClick={() => run(() => docInterpretationService.chooseQuantity(id, line.id, 'Document'))}>
              {t(`${p}.quantityDocument`, { '0': fmt(line.docQuantity) }, `Dokumentets antall (${fmt(line.docQuantity)})`)}
            </button>
            {line.remainingQuantity != null && <button type="button" disabled={busy} className={button(line.quantityChoice === 'Remaining')}
              onClick={() => run(() => docInterpretationService.chooseQuantity(id, line.id, 'Remaining'))}>
              {t(`${p}.quantityRemaining`, { '0': fmt(line.remainingQuantity) }, `Gjenstår på bestillingen (${fmt(line.remainingQuantity)})`)}
            </button>}
            <div className="flex items-center gap-2">
              <NumericInput
                mode="decimal"
                value={customQuantity}
                onChange={e => setCustomQuantity(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && Number(customQuantity) > 0)
                    run(() => docInterpretationService.chooseQuantity(id, line.id, 'Custom', Number(customQuantity)));
                }}
                placeholder={t(`${p}.quantityCustom`, {}, 'Eget antall')}
                className="w-28 px-3 py-2 border border-gray-300 rounded-lg"
              />
              <button type="button" disabled={busy || !(Number(customQuantity) > 0)} className={button(line.quantityChoice === 'Custom')}
                onClick={() => run(() => docInterpretationService.chooseQuantity(id, line.id, 'Custom', Number(customQuantity)))}>
                {t(`${p}.quantityUseCustom`, {}, 'Bruk')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Konflikt (punkt 17) */}
      {line.conflictType && editable && (
        <div className={`p-3 border rounded-lg space-y-2 ${!line.conflictChoice ? 'border-amber-200 bg-amber-50' : 'border-gray-200'}`}>
          <div className="font-semibold text-gray-800">{conflictText(line.conflictType)}</div>
          {line.conflictDetail && <div className="text-gray-600">{line.conflictDetail}</div>}
          {line.conflictType !== 'OtherDocumentNo' && (
            <div className="text-gray-600">
              {t(`${p}.conflictKeepWarehouse`, {}, 'Lagerets arbeid røres ikke. Hold linjen utenfor, eller koble den til en annen bestillingslinje.')}
            </div>
          )}
          <div className="flex gap-2">
            {/* «Erstatt» bare for annet dokumentnummer (TW 36423839) */}
            {line.conflictType === 'OtherDocumentNo' && (
              <button type="button" disabled={busy} className={button(line.conflictChoice === 'Replace')}
                onClick={() => run(() => docInterpretationService.chooseConflict(id, line.id, 'Replace'))}>
                {t(`${p}.conflictReplace`, {}, 'Erstatt')}
              </button>
            )}
            <button type="button" disabled={busy} className={button(line.conflictChoice === 'Exclude')}
              onClick={() => run(() => docInterpretationService.chooseConflict(id, line.id, 'Exclude'))}>
              {t(`${p}.exclude`, {}, 'Hold utenfor')}
            </button>
          </div>
        </div>
      )}

      {/* Kobling (punkt 18) */}
      {editable && (notFound || linked || newOrderLine) && !line.excluded && (
        <div className={`p-3 border rounded-lg space-y-2 ${notFound ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
          <div className="font-semibold text-gray-800">
            {notFound && line.checks.includes('ItemNotOnOrder')
              ? t(`${p}.notOnOrderTitle`, {}, 'Varen står ikke på bestillingen — koble den til en annen bestillingslinje, eller opprett en ny')
              : notFound
                ? t(`${p}.notFoundTitle`, {}, 'Linjen er ikke funnet — koble til en bestillingslinje')
                : t(`${p}.relinkTitle`, {}, 'Koble til en annen bestillingslinje')}
          </div>
          <div className="flex gap-2">
            <ScanTextInput
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') search(); }}
              placeholder={t(`${p}.searchPlaceholder`, {}, 'Varenr., beskrivelse, EAN eller bestillingsnr.')}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
            />
            <button type="button" onClick={search} disabled={busy} className={button(false)}>
              <Search size={16} />
            </button>
          </div>
          {searchError && <div className="text-red-800">{searchError}</div>}
          {hits && hits.length === 0 && <div className="text-gray-500">{t(`${p}.noHits`, {}, 'Ingen åpne bestillingslinjer passer.')}</div>}
          {hits && hits.length > 0 && (
            <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg max-h-64 overflow-auto bg-white">
              {hits.map(hit => (
                <li key={`${hit.orderNo}-${hit.lineNo}`} className="flex items-center justify-between gap-2 px-3 py-2">
                  <div>
                    <div className="font-medium text-gray-800">{hit.itemNo} · {hit.description}</div>
                    <div className="text-xs text-gray-500">
                      {hit.orderNoERP || hit.orderNo} / {hit.lineNo} · {hit.supplierName} · {t(`${p}.hitQuantities`, { '0': fmt(hit.quantity), '1': fmt(hit.remaining) }, `Bestilt ${fmt(hit.quantity)} · gjenstår ${fmt(hit.remaining)}`)}
                      {hit.conflictTypes.length > 0 && <span className="ml-1 text-amber-800">· {hit.conflictTypes.map(c => conflictText(c)).join(' ')}</span>}
                    </div>
                  </div>
                  <button type="button" disabled={busy} className={button(false)}
                    onClick={() => run(() => docInterpretationService.linkLine(id, line.id, hit.orderNo, hit.lineNo))}>
                    <Link2 size={14} className="inline mr-1" />{t(`${p}.link`, {}, 'Koble')}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {notFound && (
            <div className="flex flex-wrap gap-2 pt-1">
              <button type="button" disabled={busy} className={button(false)}
                onClick={() => run(() => docInterpretationService.setExcluded(id, line.id, true))}>
                {t(`${p}.exclude`, {}, 'Hold utenfor')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* «Opprett ny bestillingslinje» (punkt 18): opprettes ved OK, på bestillingen som behandles eller en annen */}
      {editable && notFound && !line.excluded && (
        <NewOrderLinePanel result={result} line={line} run={run} busy={busy} />
      )}
      {editable && newOrderLine && !line.excluded && (editNewLine ? (
        <NewOrderLinePanel result={result} line={line} run={run} busy={busy} />
      ) : (
        <button type="button" className={button(false)} onClick={() => setEditNewLine(true)}>
          {t(`${p}.newOrderLineEdit`, {}, 'Endre bestilling eller vare for den nye linjen')}
        </button>
      ))}

      {/* Mottakslinjer: antall og batch per mottakslinje (punkt 19) */}
      {(linked || newOrderLine) && !line.excluded && editable
        && (line.batchItem || line.docBatchNo || line.receiptLines.length > 0 || line.checks.includes('BatchUncertain')) && (
        <div className="p-3 border border-gray-200 rounded-lg space-y-2">
          <div className="font-semibold text-gray-800">{t(`${p}.receiptLines`, {}, 'Mottakslinjer (én per batch)')}</div>
          <div className="flex gap-2 text-xs text-gray-500">
            <span className="w-24">{t(`${p}.receiptQuantity`, {}, 'Antall')}</span>
            <span className="flex-1">{t(`${p}.receiptBatch`, {}, 'Batch')}</span>
            <span className="w-40">{t(`${p}.receiptExpiry`, {}, 'Best før')}</span>
            <span className="w-8" />
          </div>
          {receiptDraft.map((r, index) => (
            <div key={index} className="flex items-center gap-2">
              <NumericInput mode="decimal" value={r.quantity}
                onChange={e => setReceiptDraft(receiptDraft.map((d, i) => (i === index ? { ...d, quantity: e.target.value } : d)))}
                aria-label={t(`${p}.receiptQuantity`, {}, 'Antall')}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg" />
              <ScanTextInput value={r.batchNo}
                onChange={e => setReceiptDraft(receiptDraft.map((d, i) => (i === index ? { ...d, batchNo: e.target.value } : d)))}
                placeholder={t(`${p}.receiptBatch`, {}, 'Batch')}
                maxLength={50}
                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg" />
              <input type="date" value={r.expiryDate}
                onChange={e => setReceiptDraft(receiptDraft.map((d, i) => (i === index ? { ...d, expiryDate: e.target.value } : d)))}
                aria-label={t(`${p}.receiptExpiry`, {}, 'Best før')}
                title={t(`${p}.receiptExpiry`, {}, 'Best før')}
                className="w-40 px-3 py-2 border border-gray-300 rounded-lg" />
              <button type="button" onClick={() => setReceiptDraft(receiptDraft.filter((_, i) => i !== index))}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
                aria-label={t(`${p}.receiptRemove`, {}, 'Fjern mottakslinje')}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <button type="button" className={button(false)} onClick={() => setReceiptDraft([...receiptDraft, { quantity: '', batchNo: '', expiryDate: '' }])}>
              <Plus size={14} className="inline mr-1" />{t(`${p}.receiptAdd`, {}, 'Legg til batch')}
            </button>
            <button type="button" disabled={busy || !receiptDirty} className={button(false)}
              onClick={() => run(() => docInterpretationService.setReceiptLines(id, line.id, receiptLines))}>
              {t(`${p}.receiptSave`, {}, 'Lagre mottakslinjer')}
            </button>
          </div>
          <p className="text-xs text-gray-500">{t(`${p}.receiptHelp`, {}, 'Summen blir linjens antall.')}</p>
        </div>
      )}

      {/* Hold utenfor / ta med igjen (ikke-funnet og konflikt har egne knapper over) */}
      {editable && (line.excluded || (!notFound && !line.conflictType)) && (
        <button type="button" disabled={busy} className={button(false)}
          onClick={() => run(() => docInterpretationService.setExcluded(id, line.id, !line.excluded))}>
          {line.excluded ? t(`${p}.include`, {}, 'Ta med igjen') : t(`${p}.exclude`, {}, 'Hold utenfor')}
        </button>
      )}
    </div>
  );
};
